import { useCallback, useEffect, useState } from "react";

interface IReportsListSettingsV1 {
  version: 1;
  pinnedCodes: string[];
}

type IReportsListSettings = IReportsListSettingsV1;

const CURRENT_REPORTS_LIST_SETTINGS_VERSION = 1;
const REPORTS_LIST_SETTINGS_KEY_SUFFIX = "reports_list_settings_v1";

const defaultReportsListSettings: IReportsListSettings = {
  version: CURRENT_REPORTS_LIST_SETTINGS_VERSION,
  pinnedCodes: [],
};

const isLocalStorageAvailable = localStorageSupportTest();

function localStorageSupportTest() {
  const test = "__test__";

  try {
    window.localStorage.setItem(test, test);
    window.localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}

type TLoadedReportsListSettings = Partial<IReportsListSettingsV1> & { version?: number };

function migrateReportsListSettings(loaded: TLoadedReportsListSettings): IReportsListSettingsV1 {
  return {
    version: 1,
    pinnedCodes: loaded.pinnedCodes ?? [],
  };
}

function getStorageKey(settingsKeyPrefix: string): string {
  return `${settingsKeyPrefix}_${REPORTS_LIST_SETTINGS_KEY_SUFFIX}`;
}

function toPersistedSettings(updatedSettings: IReportsListSettings): IReportsListSettingsV1 {
  return {
    ...migrateReportsListSettings(updatedSettings),
    version: CURRENT_REPORTS_LIST_SETTINGS_VERSION,
  };
}

export function useReportsListSettings(settingsKeyPrefix: string) {
  const [settings, setSettings] = useState<IReportsListSettings>(() => ({ ...defaultReportsListSettings }));

  useEffect(() => {
    if (!isLocalStorageAvailable || !settingsKeyPrefix) {
      return;
    }

    const storageKey = getStorageKey(settingsKeyPrefix);
    const rawSettings = localStorage.getItem(storageKey);

    if (!rawSettings) {
      setSettings({ ...defaultReportsListSettings });
      return;
    }

    try {
      const loadedSettings = JSON.parse(rawSettings) as TLoadedReportsListSettings;
      const migratedSettings = migrateReportsListSettings(loadedSettings);

      if (JSON.stringify(loadedSettings) !== JSON.stringify(migratedSettings)) {
        localStorage.setItem(storageKey, JSON.stringify(migratedSettings));
      }

      setSettings(migratedSettings);
    } catch {
      setSettings({ ...defaultReportsListSettings });
    }
  }, [settingsKeyPrefix]);

  const togglePin = useCallback(
    (code: string) => {
      setSettings(currentSettings => {
        const isPinned = currentSettings.pinnedCodes.includes(code);
        const pinnedCodes = isPinned
          ? currentSettings.pinnedCodes.filter(pinnedCode => pinnedCode !== code)
          : [...currentSettings.pinnedCodes, code];

        const updatedSettings = toPersistedSettings({
          version: 1,
          pinnedCodes,
        });

        if (isLocalStorageAvailable && settingsKeyPrefix) {
          localStorage.setItem(getStorageKey(settingsKeyPrefix), JSON.stringify(updatedSettings));
        }

        return updatedSettings;
      });
    },
    [settingsKeyPrefix],
  );

  const isPinned = useCallback(
    (code: string) => settings.pinnedCodes.includes(code),
    [settings.pinnedCodes],
  );

  return {
    pinnedCodes: settings.pinnedCodes,
    togglePin,
    isPinned,
  };
}
